import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { baseAPI } from '../../../config';
import settingsStore from '../../../store/settingsStore';
import {
  PHONE_STATUSES,
  DEFAULT_PHONE_STATUS,
  PHONE_CHANNELS,
  PHONE_CHANNEL_NAVEGADOR,
  DEFAULT_PHONE_CHANNEL,
  PHONE_CHANNEL_LABELS,
} from '@claimup/shared/constants/phones';
import { getPhoneStatusLabel } from './phoneUtils';

const AddMaytapiPhone = () => {
  const { addMaytapiPhone } = settingsStore();

  const [isOpen, setIsOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhoneId, setNewPhoneId] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newStatus, setNewStatus] = useState(DEFAULT_PHONE_STATUS);
  const [newCanal, setNewCanal] = useState(DEFAULT_PHONE_CHANNEL);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // El canal cambia lo que significa el Phone ID, asi que la ayuda de debajo
  // de cada campo se adapta: un alta de navegador con el id de Maytapi crea
  // una linea que no manda nunca y no da ningun error.
  const esNavegador = newCanal === PHONE_CHANNEL_NAVEGADOR;

  const handleCreatePhone = async () => {
    if (!newName.trim()) {
      toast.error('Introduce un nombre');
      return;
    }
    if (!newPhoneId.trim()) {
      toast.error('Introduce un phone_id');
      return;
    }
    if (!newPhone.trim()) {
      toast.error('Introduce un número de teléfono');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const createResponse = await axios.post(
        `${baseAPI}/settings/phones`,
        { status: newStatus, canal: newCanal, name: newName.trim(), phone_id: newPhoneId.trim(), phone_number: newPhone.trim() },
        { headers }
      );
      toast.success('Teléfono creado correctamente');

      if (createResponse?.data?.data) {
        addMaytapiPhone(createResponse.data.data);
      }

      setNewName('');
      setNewPhone('');
      setNewPhoneId('');
      setNewStatus(DEFAULT_PHONE_STATUS);
      setIsOpen(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error creating Maytapi phone:', err);
      toast.error('Error al crear el teléfono');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        type='button'
        onClick={() => setIsOpen(true)}
        className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-150'
      >
        Añadir teléfono
      </button>

      {isOpen && (
        <div className='fixed inset-0 z-50 overflow-y-auto'>
          <div className='flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0'>
            <div
              className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity'
              onClick={() => setIsOpen(false)}
            />

            <div className='relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6'>
              <div className='sm:flex sm:items-start'>
                <div className='mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left'>
                  <h3 className='text-lg font-semibold leading-6 text-gray-900'>Añadir nuevo teléfono</h3>
                  <div className='mt-4'>
                    <label className='block text-sm font-medium text-slate-700 mb-1'>Nombre</label>
                    <input
                      type='text'
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                      placeholder='Nombre descriptivo'
                      className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm mb-4'
                    />
                    <label className='block text-sm font-medium text-slate-700 mb-1'>Canal</label>
                    <select
                      value={newCanal}
                      onChange={e => setNewCanal(e.target.value)}
                      className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm mb-1 bg-white text-slate-900'
                    >
                      {PHONE_CHANNELS.map(canal => (
                        <option key={canal} value={canal}>
                          {PHONE_CHANNEL_LABELS[canal]}
                        </option>
                      ))}
                    </select>
                    {/* El canal decide qué significa el Phone ID, y son dos cosas
                        distintas: en Maytapi es el id numérico de su panel; en
                        navegador es el nombre de la carpeta del perfil de Chrome.
                        Decirlo aquí evita el alta que no manda nunca. */}
                    <p className='text-xs text-slate-500 mb-4'>
                      {esNavegador
                        ? 'Sale por una sesión de Chrome del brazo, con su presupuesto ganado.'
                        : 'Sale por la API de Maytapi, con el tope fijo de la ficha.'}
                    </p>

                    <label className='block text-sm font-medium text-slate-700 mb-1'>Phone ID</label>
                    <input
                      type='text'
                      value={newPhoneId}
                      onChange={e => setNewPhoneId(e.target.value)}
                      placeholder={esNavegador ? 'Nombre de la carpeta del perfil, p. ej. Persona 1' : 'maytapi phone_id'}
                      className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm mb-1'
                    />
                    <p className='text-xs text-slate-500 mb-4'>
                      {esNavegador
                        ? 'Tiene que coincidir EXACTAMENTE con la carpeta de `.wa-perfil\\`: es como el brazo encuentra su ficha.'
                        : 'El identificador que da Maytapi en su panel.'}
                    </p>
                    <label className='block text-sm font-medium text-slate-700 mb-1'>Número de teléfono</label>
                    <input
                      type='text'
                      value={newPhone}
                      onChange={e => setNewPhone(e.target.value)}
                      placeholder='+34 600 000 000'
                      className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm mb-4'
                    />
                    <label className='block text-sm font-medium text-slate-700 mb-1'>Estado</label>
                    <select
                      value={newStatus}
                      onChange={e => setNewStatus(e.target.value)}
                      className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm bg-white text-slate-900'
                    >
                      {PHONE_STATUSES.map(status => (
                        <option key={status} value={status}>
                          {getPhoneStatusLabel(status)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className='mt-6 sm:mt-8 sm:flex sm:flex-row-reverse'>
                <button
                  type='button'
                  onClick={handleCreatePhone}
                  disabled={isSubmitting}
                  className='inline-flex w-full justify-center rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isSubmitting ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                  type='button'
                  onClick={() => setIsOpen(false)}
                  className='mt-3 inline-flex w-full justify-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto'
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddMaytapiPhone;
